import moment from "moment-timezone";

// Curated, DST-safe IANA time zone list.
// Replaces the old (UTC±X) offset + full moment-timezone list, which drifted
// out of sync whenever a locale's DST rules changed. IANA zone ids already
// encode DST transitions, so the offset never needs to be stored separately.
const TIMEZONE_GROUPS = [
  {
    header: "Americas",
    zones: [
      "America/Anchorage",
      "Pacific/Honolulu",
      "America/Los_Angeles",
      "America/Denver",
      "America/Chicago",
      "America/New_York",
      "America/Toronto",
      "America/Sao_Paulo",
    ],
  },
  {
    header: "Europe",
    zones: [
      "Europe/London",
      "Europe/Paris",
      "Europe/Berlin",
      "Europe/Madrid",
      "Europe/Rome",
      "Europe/Moscow",
    ],
  },
  {
    header: "Africa",
    zones: ["Africa/Cairo", "Africa/Nairobi", "Africa/Johannesburg"],
  },
  {
    header: "Middle East",
    zones: ["Asia/Dubai", "Asia/Riyadh"],
  },
  {
    header: "Asia",
    zones: [
      "Asia/Kolkata",
      "Asia/Bangkok",
      "Asia/Singapore",
      "Asia/Manila",
      "Asia/Tokyo",
      "Asia/Seoul",
      "Asia/Hong_Kong",
    ],
  },
  {
    header: "Oceania",
    zones: [
      "Australia/Perth",
      "Australia/Brisbane",
      "Australia/Sydney",
      "Pacific/Auckland",
    ],
  },
];

// Flat list of every curated IANA zone id, no headers.
export const CURATED_TIMEZONES = TIMEZONE_GROUPS.reduce(
  (acc, group) => acc.concat(group.zones),
  []
);

// Ready to bind to a Vuetify v-select/v-autocomplete `:items`.
// Group header entries use Vuetify's `{ header }` convention and are
// rendered as non-selectable subheaders.
export const TIMEZONE_ITEMS = TIMEZONE_GROUPS.reduce((acc, group, index) => {
  if (index > 0) acc.push({ divider: true });
  acc.push({ header: group.header });
  group.zones.forEach((tz) => acc.push({ name: tz, timezone: tz }));
  return acc;
}, []);

// Legacy/non-curated zone -> nearest curated zone, used to migrate values
// that were stored before this list existed (POSIX aliases, Windows
// TimeZoneInfo ids, retired IANA aliases, or curated-adjacent zones).
// This list was built from the actual distinct values found in the
// Seminar.Timezone, InstructorLocations.TimeZone and SiteUser.Timezone
// columns (see CuratedIanaTimeZoneMigration.sql) - extend as older stored
// values surface elsewhere.
export const LEGACY_TIMEZONE_MAP = {
  // POSIX / Windows / other generic aliases
  PST8PDT: "America/Los_Angeles",
  MST7MDT: "America/Denver",
  CST6CDT: "America/Chicago",
  EST5EDT: "America/New_York",
  HST: "Pacific/Honolulu",
  EST: "America/New_York",
  MST: "America/Denver",
  GMT: "Europe/London",
  CET: "Europe/Berlin",
  UCT: "Europe/London",
  UTC: "Europe/London",
  NZ: "Pacific/Auckland",
  Eire: "Europe/London",
  Japan: "Asia/Tokyo",
  Singapore: "Asia/Singapore",
  "Eastern Standard Time": "America/New_York",
  "Pacific Standard Time": "America/Los_Angeles",
  "Pakistan Standard Time": "Asia/Dubai",
  "Coordinated Universal Time": "Europe/London",
  Pacific: "America/Los_Angeles", // confirmed: shorthand for "Pacific Time (US & Canada)"; ~98.6% of Seminar.Timezone rows

  // US/* aliases
  "US/Pacific": "America/Los_Angeles",
  "US/Eastern": "America/New_York",
  "US/Central": "America/Chicago",
  "US/Mountain": "America/Denver",
  "US/Arizona": "America/Denver",
  "US/Alaska": "America/Anchorage",
  "US/Hawaii": "Pacific/Honolulu",
  "US/Samoa": "Pacific/Honolulu", // UTC-11: closer to Honolulu (UTC-10) than Auckland (UTC+12/13)
  "US/East-Indiana": "America/New_York",

  // Canada/* aliases
  "Canada/Atlantic": "America/Toronto",
  "Canada/Central": "America/Chicago",
  "Canada/Eastern": "America/Toronto",
  "Canada/Mountain": "America/Denver",
  "Canada/Newfoundland": "America/Toronto",
  "Canada/Pacific": "America/Los_Angeles",
  "Canada/Saskatchewan": "America/Chicago", // fixed UTC-6, no DST: matches Chicago's winter offset

  // Brazil/* aliases
  "Brazil/East": "America/Sao_Paulo",
  "Brazil/West": "America/Sao_Paulo",

  // Australia/* aliases
  "Australia/ACT": "Australia/Sydney",
  "Australia/Canberra": "Australia/Sydney",
  "Australia/Melbourne": "Australia/Sydney",
  "Australia/Hobart": "Australia/Sydney",
  "Australia/Adelaide": "Australia/Brisbane",
  "Australia/Darwin": "Australia/Brisbane",
  "Australia/Queensland": "Australia/Brisbane",
  "Australia/West": "Australia/Perth",

  // Etc/* (POSIX sign-reversed GMT offsets)
  "Etc/GMT": "Europe/London",
  "Etc/GMT+0": "Europe/London",
  "Etc/GMT+1": "Europe/London", // Etc/GMT+1 == UTC-1
  "Etc/GMT+2": "America/Sao_Paulo", // Etc/GMT+2 == UTC-2
  "Etc/GMT-1": "Europe/Berlin", // Etc/GMT-1 == UTC+1
  "Etc/Greenwich": "Europe/London",

  // Non-curated IANA zones -> closest curated major zone (Americas)
  "America/Tijuana": "America/Los_Angeles",
  "America/Vancouver": "America/Los_Angeles",
  "America/Chihuahua": "America/Denver",
  "America/Mazatlan": "America/Denver",
  "America/Creston": "America/Denver",
  "America/Dawson_Creek": "America/Denver",
  "America/Edmonton": "America/Denver",
  "America/Mexico_City": "America/Chicago",
  "America/Monterrey": "America/Chicago",
  "America/Guatemala": "America/Chicago",
  "America/Costa_Rica": "America/Chicago",
  "America/Regina": "America/Chicago",
  "America/Winnipeg": "America/Chicago",
  "America/North_Dakota/Beulah": "America/Chicago",
  "America/Bogota": "America/New_York",
  "America/Lima": "America/New_York",
  "America/Detroit": "America/New_York",
  "America/Montreal": "America/Toronto",
  "America/Thunder_Bay": "America/Toronto",
  "America/Pangnirtung": "America/Toronto",
  "America/Caracas": "America/Toronto",
  "America/La_Paz": "America/Toronto",
  "America/Santiago": "America/Sao_Paulo",
  "America/Buenos_Aires": "America/Sao_Paulo",
  Greenland: "America/Sao_Paulo",
  "Atlantic/Stanley": "America/Sao_Paulo",
  "Pacific/Rarotonga": "Pacific/Honolulu", // UTC-10, exact match
  "Pacific/Midway": "Pacific/Auckland",

  // Non-curated -> closest curated major zone (Europe/Africa)
  "Atlantic/Azores": "Europe/London",
  "Atlantic/Cape_Verde": "Europe/London",
  "Africa/Casablanca": "Europe/London",
  "Europe/Dublin": "Europe/London",
  "Europe/Lisbon": "Europe/London",
  "Europe/Jersey": "Europe/London",
  "Africa/Monrovia": "Europe/London",
  "Africa/Abidjan": "Europe/London",
  "Africa/Accra": "Europe/London",
  "Africa/Bamako": "Europe/London",
  "Europe/Amsterdam": "Europe/Berlin",
  "Europe/Belgrade": "Europe/Berlin",
  "Europe/Bratislava": "Europe/Berlin",
  "Europe/Brussels": "Europe/Berlin",
  "Europe/Budapest": "Europe/Berlin",
  "Europe/Copenhagen": "Europe/Berlin",
  "Europe/Ljubljana": "Europe/Berlin",
  "Europe/Luxembourg": "Europe/Berlin",
  "Europe/Prague": "Europe/Berlin",
  "Europe/Sarajevo": "Europe/Berlin",
  "Europe/Skopje": "Europe/Berlin",
  "Europe/Stockholm": "Europe/Berlin",
  "Europe/Vienna": "Europe/Berlin",
  "Europe/Warsaw": "Europe/Berlin",
  "Europe/Zagreb": "Europe/Berlin",
  "Europe/Zurich": "Europe/Berlin",
  "Africa/Ceuta": "Europe/Berlin",
  "Africa/Tunis": "Europe/Berlin",
  "Europe/Athens": "Europe/Moscow",
  "Europe/Bucharest": "Europe/Moscow",
  "Europe/Helsinki": "Europe/Moscow",
  "Europe/Istanbul": "Europe/Moscow",
  "Europe/Kiev": "Europe/Moscow",
  "Europe/Minsk": "Europe/Moscow",
  "Europe/Riga": "Europe/Moscow",
  "Europe/Sofia": "Europe/Moscow",
  "Europe/Tallinn": "Europe/Moscow",
  "Europe/Vilnius": "Europe/Moscow",
  "Europe/Volgograd": "Europe/Moscow",
  "Africa/Harare": "Africa/Johannesburg",
  "Africa/Blantyre": "Africa/Johannesburg",
  "Africa/Brazzaville": "Africa/Johannesburg",
  "Africa/Bujumbura": "Africa/Johannesburg",
  "Africa/Lusaka": "Africa/Johannesburg",
  "Africa/Addis_Ababa": "Africa/Nairobi",
  "Africa/Asmara": "Africa/Nairobi",
  "Africa/Asmera": "Africa/Nairobi",
  "Africa/Dar_es_Salaam": "Africa/Nairobi",
  "Africa/Djibouti": "Africa/Nairobi",

  // Non-curated -> closest curated major zone (Middle East/Asia/Oceania)
  "Asia/Baghdad": "Asia/Riyadh",
  "Asia/Kuwait": "Asia/Riyadh",
  "Asia/Qatar": "Asia/Riyadh",
  "Asia/Jerusalem": "Asia/Dubai",
  "Asia/Muscat": "Asia/Dubai",
  "Asia/Tbilisi": "Asia/Dubai",
  "Asia/Yerevan": "Asia/Dubai",
  "Asia/Baku": "Asia/Dubai",
  "Asia/Tehran": "Asia/Dubai",
  "Asia/Kabul": "Asia/Dubai",
  "Asia/Karachi": "Asia/Dubai",
  "Asia/Tashkent": "Asia/Dubai",
  "Indian/Mauritius": "Asia/Dubai",
  "Asia/Aqtobe": "Asia/Kolkata",
  "Asia/Kathmandu": "Asia/Kolkata",
  "Asia/Yekaterinburg": "Asia/Kolkata",
  "Asia/Almaty": "Asia/Kolkata",
  "Asia/Dhaka": "Asia/Kolkata",
  "Asia/Novosibirsk": "Asia/Bangkok",
  "Asia/Jakarta": "Asia/Bangkok",
  "Asia/Krasnoyarsk": "Asia/Bangkok",
  "Asia/Brunei": "Asia/Singapore",
  "Asia/Kuala_Lumpur": "Asia/Singapore",
  "Asia/Chongqing": "Asia/Hong_Kong",
  "Asia/Taipei": "Asia/Hong_Kong",
  "Asia/Ulaanbaatar": "Asia/Hong_Kong",
  "Asia/Urumqi": "Asia/Hong_Kong",
  "Asia/Shanghai": "Asia/Hong_Kong",
  "Asia/Irkutsk": "Asia/Seoul",
  "Asia/Yakutsk": "Australia/Brisbane",
  "Pacific/Guam": "Australia/Sydney",
  "Pacific/Port_Moresby": "Australia/Brisbane",
  "Asia/Vladivostok": "Australia/Sydney",
  "Asia/Magadan": "Pacific/Auckland",
  "Pacific/Fiji": "Pacific/Auckland",
};

// Normalizes a stored zone value to a curated IANA zone without changing
// the wall-clock time it represents:
//  - strips a legacy "(UTC±HH:MM) " prefix, keeping the IANA id
//  - maps legacy/POSIX/Windows aliases and non-curated IANA zones to the
//    nearest curated zone
//  - already-curated zones pass through unchanged
//  - anything still unrecognized is returned unchanged so the caller can
//    decide how to handle it
export function normalizeToCuratedTimezone(rawValue) {
  if (!rawValue) return rawValue;
  const stripped = rawValue.replace(/^\(UTC[^)]*\)\s*/i, "").trim();
  if (CURATED_TIMEZONES.includes(stripped)) return stripped;
  return LEGACY_TIMEZONE_MAP[stripped] || stripped;
}

// Returns the first candidate that resolves to a zone moment-timezone knows,
// falling back to the browser's zone. Used to convert stored UTC seminar times
// for display/save: `moment.utc(x).tz(badValue)` returns undefined for an
// empty value, which used to abort the whole date/time load in the edit form.
// Valid IANA ids are used as-is (after stripping a legacy "(UTC±HH:MM) "
// prefix) so the conversion is exact; the curated mapping is only a fallback
// for values moment can't read (e.g. Windows ids).
export function resolveValidTimezone(...candidates) {
  for (const raw of candidates) {
    if (!raw || typeof raw !== "string") continue;
    const stripped = raw.replace(/^\(UTC[^)]*\)\s*/i, "").trim();
    if (moment.tz.zone(stripped)) return stripped;
    const mapped = normalizeToCuratedTimezone(stripped);
    if (mapped && moment.tz.zone(mapped)) return mapped;
  }
  return moment.tz.guess();
}
